import { AppLauncher } from "@src-capacitor/@capacitor/app-launcher"
import { Log } from "src/util/classes/log"
import { COLOR_LIST } from "src/util/constants/colorList"
import { PriorityClass } from "src/util/types/misc"

/**
 * Linear interpolation between 2 points, gives a value T.
 * In layman's terms - return a value based on T, that is between the 2 points.
 *
 * @param v0 - Point 1.
 * @param v1 - Point 2.
 * @param t - Value between [0, 1].
 */
export function lerp(v0: number, v1: number, t: number) {
  return v0 + t * (v1 - v0)
}

/**
 * Takes a value short or long hex colour string and returns an array of RGB values.
 *
 * @param hexString - valid short or long colour hex string.
 */
export function hexToRgb(hexString: string): Array<number> {
  hexString = hexString.slice(1) // Remove the #

  if (hexString.length === 3) {
    return hexString
      .split("") // Split on every character
      .map((x) => Number.parseInt(x + x, 16)) // To convert to full form - just multiply each value by 2
  } else {
    return hexString
      .split(/(?=(?:..)*$)/) // Split on even characters
      .map((x) => Number.parseInt(x, 16))
  }
}

/**
 * Returns the RGB value of the percentage between the two provided colours.
 *
 * @param color1
 * @param color2
 * @param percentage
 * @return - Format is: ***rgb(v1, v2, v3)***.
 */
export function calculateRGBLerp(
  color1: string,
  color2: string,
  percentage: number
): [number, number, number] {
  const [r1, g1, b1] = hexToRgb(color1)
  const [r2, g2, b2] = hexToRgb(color2)

  const r = lerp(r1, r2, percentage)
  const g = lerp(g1, g2, percentage)
  const b = lerp(b1, b2, percentage)

  return [r, g, b]
}

/**
 * Transforms the colour list to a gradient and then returns the appropriate colour based on the provided percentage.
 *
 * @param percentage - Expected value is [0, 1].
 * @param colorList - Order matters.
 */
export function getColorFromGradient(percentage: number, colorList: string[]) {
  // There are no colours, so we don't deal with anything.
  if (colorList.length === 0) return

  // If only one colour, return said colour.
  if (colorList.length === 1) {
    const [r, g, b] = hexToRgb(colorList[0])
    return [r, g, b]
  }

  const numberOfColors = colorList.length - 1

  for (const [index, color] of colorList.entries()) {
    if (index === 0) continue // skip first one

    const minVal = roundTo2((index - 1) / numberOfColors)
    const maxVal = roundTo2(index / numberOfColors)

    if (percentage >= minVal && percentage <= maxVal) {
      // The range in which we want to look for colours.
      // F.x. if we have 3 colours, we want the first one to be at 0, the second to be at 0.5 and the third at 1.
      // This will return the appropriate range, which will be used to adjust the percentages.
      // In the example with 3 colours - it would return either 0 or 0.5.
      // If we have 4 - it will be 0, 0.33, 0.66 and 1.
      const adjustedRange = (index - 1) / numberOfColors

      // Adjusted percentage based on the specified region.
      // In order for smooth transition between colours, we need to have 0 to 100% transition between each colour region.
      // This will adjust the current percentage to map from 0 to 1 based on the selected region.
      const adjustedPercentage = (percentage - adjustedRange) * numberOfColors

      // Returns the RGB values based on the percentage between the previous colour in the list and the current one.
      return calculateRGBLerp(colorList[index - 1], color, adjustedPercentage)
    }
  }
}

/**
 * Round to 2 decimal places.
 *
 * @param num
 */
export function roundTo2(num: number) {
  // Add EPSILON to adjusted for 0.5 case.
  return Math.round((num + Number.EPSILON) * 100) / 100
}

/**
 * Checks if the provided value is an empty string, undefined or null.
 *
 * @param val
 */
export function isEmptyOrNull(val: string | null | undefined | number): boolean {
  // We check for == null as this also includes undefined
  return val == null || val === ""
}

/**
 * Checks for the number of digits after the decimal point (precision).
 * If the input is not a float, then it returns null.
 * Note: Strings with numeric separators don't work for some reason.
 * @param val
 */
export function getPrecision(val: string | number): number | null {
  if (Number.isNaN(val) || !Number.isFinite(Number(val))) return null

  // So we fix exponents
  val = Number(val)

  const valueAfterDecimal = val.toString().split(".")[1]
  if (!valueAfterDecimal) return null

  return valueAfterDecimal.length
}

/**
 * Gives the smallest number based on number of decimals.
 * @param decimals
 */
export function decimalStep(decimals: number): string {
  return (0.1 ** decimals).toFixed(decimals)
}

export function blobToBase64(blob: Blob): Promise<string | ArrayBuffer | null> {
  const reader = new FileReader()
  reader.readAsDataURL(blob)
  return new Promise((resolve) => {
    reader.addEventListener("load", () => {
      resolve(reader.result)
    })
  })
}

export function base64ToBlob(base64: string, type: string): Promise<Blob> {
  return fetch(`data:${type};base64,${base64}`).then((response) => response.blob())
}

// Escapes bad HTML characters
export function escapeHTML(badString: string) {
  return badString
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;")
}

/**
 * Filter out `undefined` values from a `T | undefined` type object array
 *
 * @param objArray
 */
export function filterOutUndefined<T>(objArray: (T | undefined)[]): T[] {
  return objArray.filter((obj: T | undefined): obj is T => obj !== undefined)
}

export function getContrastingColorForBackground(background: string): string {
  const [r, g, b] = hexToRgb(background)
  // Based on the wild magic of this thread:
  // https://gamedev.stackexchange.com/questions/38536/given-a-rgb-color-x-how-to-find-the-most-contrasting-color-y/38542#38542
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.5 ? "#000" : "#FFF"
}

/**
 * Extracts the initials of the full name provided.
 * If the number of names is more than the max number of initials,
 * then it takes enough initials from the first names and the last one,
 * to satisfy the max initial rule.
 * Works only on latin characters.
 *
 * @param fullName The string on which initials are to be extracted
 * @param delimiter What to put between the initials
 * @param maxInitials The maximum number of initials extracted
 *
 * @returns Capitalised initials with specified delimiter. Returns undefined if string is bad.
 */
export function extractInitials(
  fullName: string,
  delimiter = "",
  maxInitials = 3
): string | undefined {
  // Invalid input
  if (!fullName) return undefined

  /**
   * Detect only latin characters in the beginning of a string or between whitespace.
   *
   * Regex explanation:
   *
   * (?<=B)A - Positive look behind - Find expression A where expression B precedes.
   * ^ - Start of string.
   * \s - Any white space character.
   *
   * (?<=^|\s) - Find all expressions that are at the begging of the string or
   * have whitespace before them. Does not consume characters.
   *
   * ([a-zA-Z]) - Capture group will take only latin characters.
   *
   * /g - Do not stop after first match.
   */
  const initialsRegex = /(?<=^|\s)([A-Za-z])/g
  let regexMatches: string[] | null = fullName.match(initialsRegex)

  // No matches found
  if (regexMatches === null || regexMatches.length === 0) return undefined

  if (regexMatches.length > maxInitials) {
    const lastInitial = regexMatches.pop()
    // Remove enough so that when we add the last element, it satisfies the max initials
    regexMatches = regexMatches.splice(0, maxInitials - 1)

    // We know lastInitial is not undefined, but pop typing requires us to check
    if (lastInitial) {
      regexMatches.push(lastInitial)
    }
  }

  let initials = regexMatches.map((initial: string) => initial.toUpperCase()).join(delimiter)

  // In case we have non-empty delimiter - put it also at the end. F.x. - J.D.
  if (delimiter) initials += delimiter

  return initials
}

/**
 * Determines the color of the profile picture if initials are used.
 * On fail or any other case, returns "grey".
 *
 * TODO: Unit test me
 */
export function determineProfilePictureColor(initials: string) {
  if (COLOR_LIST.length > 0) {
    const sum = [...initials].reduce((partialSum, a) => {
      const charCode = a.codePointAt(0)
      return charCode ? partialSum + charCode : 0
    }, 0)
    const colorIndex = sum % COLOR_LIST.length
    const selectedColor = COLOR_LIST[colorIndex]

    if (selectedColor) return selectedColor
  }
  return "grey"
}

/**
 * Converts a `number` to one of five priority classes.
 * The function will accept any number, but is intended for numbers in the range 1-100.
 *  1 - 20:  priority1
 * 21 - 40:  priority2
 * 41 - 60:  priority3
 * 61 - 80:  priority4
 * 81 - 100: priority5
 */
export function numberToPriorityClass(number: number): PriorityClass {
  if (number < 0) {
    Log.warning({ message: `priority number out of range [1 - 100]: ${number}` })
    return "priority1"
  }
  if (100 < number) {
    Log.warning({ message: `priority number out of range [1 - 100]: ${number}` })
    return "priority5"
  }

  if (number < 21) {
    return "priority1"
  } else if (number < 41) {
    return "priority2"
  } else if (number < 61) {
    return "priority3"
  } else if (number < 81) {
    return "priority4"
  } else {
    return "priority5"
  }
}

/**
 * Helper function in which you can pass any primiteve type and search term,
 * and it will return a boolean if the term is contained in the value.
 * If the value is null or undefined, it automatically returns false.
 * Used primaraly as a simple search substitude for SearchMaps in technician mode.
 * Based on search functionality in SfxTable.
 *
 * @param value
 * @param term
 */
export function primitiveValueSearch(
  value: string | number | boolean | null | undefined,
  term: string
): boolean {
  // Term never matches unset fields.
  if (value === undefined || value === null) {
    return false
  }

  // Look for term in value. Using lower casing to make matches more likely.
  const haystack = String(value).toLowerCase()
  const lowerTerms = term.toLowerCase()

  return haystack.includes(lowerTerms)
}

/**
 * Detects if we have any of the "supported" browsers installed on the device.
 *
 * **NB!** This will return TRUE if the app is installed regardless if it is disabled or not.
 * If the app is disabled, then we cannot use it.
 */
export async function isBrowserInstalled(): Promise<boolean> {
  // List of application names for supported browsers.
  // You can easily find the name, by f.x. going to the web version of play store, then to the browser in question
  // and checking the URL. It should look something like play.google.com/store/apps/details?id=The_App_Name.
  // These are just the main browsers. Each of them has they own additional versions/flavours
  // like Canary, Dev, Nightly, etc. But checking only main ones would suffice 99% of the time,
  // and we can "easily" detect the issue in Sentry otherwise.
  // Sentry Exception ->
  // Unable to start activity ComponentInfo{com.safeex.cloud/com.capacitorjs.plugins.browser.BrowserControllerActivity}:
  // android.content.ActivityNotFoundException: No Activity found to handle Intent
  // { act=android.intent.action.VIEW dat=https://safeex.cloud/ (SB Link)
  const supportedBrowsers = [
    // Google Chrome
    "com.android.chrome",
    // Mozilla FireFox
    "org.mozilla.firefox",
    // Microsoft Edge
    "com.microsoft.emmx",
    // Samsung Internet (iSafe devices are internally Samsung tablets/phones)
    "com.sec.android.app.sbrowser",
  ]

  try {
    for (const url of supportedBrowsers) {
      const { value } = await AppLauncher.canOpenUrl({ url })

      // We already have a browser to open with, just skip the rest.
      if (value) return true
    }

    // No browser found. What is this mythical/paranoid user??? Just why?
    return false
  } catch (error) {
    Log.error({ error, message: "isBrowserInstalled failed" })
    return false
  }
}
